import type {
  EvalPromptProductionMetrics,
  EvalPromptRanking,
  EvalPromptTheme,
  EvalResultsScopeTemplate,
} from "@api/rest/types-and-hooks";
import { TemplateStatus } from "@pages/Templates/constants";

/** Days of reports the production metrics cover: pipelines' `WINDOW_DAYS`
 *  (eval_prompt_org_metrics_workflow.py), the same window as the themes. */
export const METRICS_WINDOW_DAYS = 90;
export const METRICS_WINDOW_LABEL = `last ${METRICS_WINDOW_DAYS} days`;

/** A value already on 0–100 as a whole percent. */
export const formatPercent = (value: number): string => `${Math.round(value)}%`;

const DAY_MS = 24 * 60 * 60 * 1000;

/** An accepted suggestion's window: its "now" rates are the last 90 days, so
 *  they're all post-PR only once 90 days have passed since it opened. */
export const acceptedWindowLabel = (shippedAt: string | null, now: number = Date.now()): string => {
  if (!shippedAt) return `Last ${METRICS_WINDOW_DAYS} days`;
  const days = Math.max(0, Math.floor((now - new Date(shippedAt).getTime()) / DAY_MS));
  return days >= METRICS_WINDOW_DAYS
    ? `Last ${METRICS_WINDOW_DAYS} days, all since the PR opened`
    : `Last ${METRICS_WINDOW_DAYS} days · day ${days} of ${METRICS_WINDOW_DAYS} since the PR opened`;
};

/** "62% → 48%", with a dash for a side with no number. */
export const rateChange = (before: number | null, after: number | null): string =>
  `${before == null ? "—" : formatPercent(before)} → ${after == null ? "—" : formatPercent(after)}`;

/** The template filter's choices: those with production rows for the org
 *  picked, or for any org. */
export const scopeTemplates = (
  templates: EvalResultsScopeTemplate[],
  orgId: number | null,
): EvalResultsScopeTemplate[] =>
  orgId === null ? templates : templates.filter((t) => t.organization_ids.includes(orgId));

/** "#12 — Initial Assessment", with the status when it isn't published: an
 *  archived version's rows still count. */
export const scopeTemplateLabel = (template: EvalResultsScopeTemplate): string =>
  `#${template.id} — ${template.name}${template.status === TemplateStatus.PUBLISHED ? "" : ` (${template.status})`}`;

/** Eval Results' scope lives in its URL, so a link can return to an org. */
export const EVAL_RESULTS_ORG_PARAM = "organization";
export const EVAL_RESULTS_TEMPLATE_PARAM = "template";

export const evalResultsPath = (organizationId: number): string =>
  `/eval-results?${EVAL_RESULTS_ORG_PARAM}=${organizationId}`;

/** An id from the URL; anything but a positive integer is none. */
export const idParam = (value: string | null): number | null => {
  const id = Number(value);
  return value && Number.isInteger(id) && id > 0 ? id : null;
};

export const ProductionSort = {
  Prompt: "prompt",
  Fields: "fields",
  Edited: "edited",
  EditSize: "editSize",
  Rewrite: "rewrite",
  Quality: "quality",
} as const;
export type ProductionSortValue = (typeof ProductionSort)[keyof typeof ProductionSort];

export const SortDirection = { Asc: "asc", Desc: "desc" } as const;
export type SortDirectionValue = (typeof SortDirection)[keyof typeof SortDirection];

/** The direction a column opens in: most concerning rows first. */
export const WORST_FIRST: Record<ProductionSortValue, SortDirectionValue> = {
  [ProductionSort.Prompt]: SortDirection.Asc,
  [ProductionSort.Fields]: SortDirection.Desc,
  [ProductionSort.Edited]: SortDirection.Desc,
  [ProductionSort.EditSize]: SortDirection.Desc,
  [ProductionSort.Rewrite]: SortDirection.Desc,
  [ProductionSort.Quality]: SortDirection.Asc,
};

export interface ProductionRow {
  prompt_key: string;
  /** Null for a global prompt. */
  owner: string | null;
  production: EvalPromptProductionMetrics;
}

export const productionRows = (rankings: EvalPromptRanking[]): ProductionRow[] =>
  rankings.flatMap((r) =>
    r.production ? [{ prompt_key: r.prompt_key, owner: r.owner_organization_name, production: r.production }] : [],
  );

/** Null when the pipeline hasn't sent the value for this row. */
const sortValue = (row: ProductionRow, key: ProductionSortValue): number | string | null => {
  const p = row.production;
  if (key === ProductionSort.Prompt) return row.prompt_key;
  if (key === ProductionSort.Fields) return p.sample_size;
  if (key === ProductionSort.EditSize) return p.avg_edit_percentage;
  if (key === ProductionSort.Rewrite) return p.pct_fields_total_rewrite;
  if (key === ProductionSort.Quality) return p.avg_quality_score;
  return p.pct_fields_edited;
};

/** Sorted copy; missing values go last either way, and ties fall back to
 *  prompt key so the order is stable. */
export const sortProductionRows = (
  rows: ProductionRow[],
  key: ProductionSortValue,
  direction: SortDirectionValue,
): ProductionRow[] => {
  const sign = direction === SortDirection.Asc ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = sortValue(a, key);
    const bv = sortValue(b, key);
    if (av === null || bv === null) {
      return Number(av === null) - Number(bv === null) || a.prompt_key.localeCompare(b.prompt_key);
    }
    const primary = typeof av === "string" ? av.localeCompare(String(bv)) : av - (bv as number);
    return sign * primary || a.prompt_key.localeCompare(b.prompt_key);
  });
};

/** How widely a theme shows: reports within the org when scoped to one,
 *  orgs otherwise — the backend switches what `support` counts. */
export const themeSupportLabel = (theme: EvalPromptTheme, orgScoped: boolean): string =>
  `${theme.support} of ${theme.group_size} ${orgScoped ? "reports" : "orgs"}`;

export interface ProductionSummary {
  prompts: number;
  fields: number;
  /** 0–100, weighted by fields. */
  edited: number;
  /** 0–100, weighted by fields. */
  editSize: number;
  /** 0–1, weighted by fields. */
  quality: number;
}

/** Field-weighted rollup, so a heavily used prompt counts for more than a rare one. */
export const productionSummary = (rows: ProductionRow[]): ProductionSummary | null => {
  const fields = rows.reduce((total, r) => total + r.production.sample_size, 0);
  if (fields === 0) return null;
  const weighted = (pick: (p: EvalPromptProductionMetrics) => number) =>
    rows.reduce((total, r) => total + pick(r.production) * r.production.sample_size, 0) / fields;
  return {
    prompts: rows.length,
    fields,
    edited: weighted((p) => p.pct_fields_edited),
    editSize: weighted((p) => p.avg_edit_percentage),
    quality: weighted((p) => p.avg_quality_score),
  };
};
